import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { ShieldCheck, Lock, Loader2, RotateCcw, Sun } from 'lucide-react';
import { usePinGate } from '../hooks/usePinGate';

const DIGIT_BUTTONS = ['1', '2', '3', '4', '5', '6', '7', '8', '9', '0'];

const formatLockedUntil = (timestamp: string | null): string | null => {
  if (!timestamp) return null;
  const date = new Date(timestamp);
  if (Number.isNaN(date.getTime())) return null;
  return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
};

const PinPage: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const {
    submitPin,
    refreshStatus,
    isLoading,
    isSubmitting,
    isPinValidated,
    attemptsRemaining,
    lockedUntil,
    helloUrl,
  } = usePinGate();

  const [pin, setPin] = useState('');
  const [shake, setShake] = useState(false);
  // Compact mode for short viewports (iOS Safari chrome/keyboard shrinks the
  // usable height well below the physical screen). Kept in sync with
  // visualViewport so the keyboard appearing mid-entry recomputes instantly.
  const [compact, setCompact] = useState(false);
  const [tight, setTight] = useState(false);
  useEffect(() => {
    const h = () => window.visualViewport?.height ?? window.innerHeight;
    const compute = () => {
      const height = h();
      setCompact(height < 700);
      setTight(height < 620);
    };
    compute();
    window.visualViewport?.addEventListener('resize', compute);
    window.addEventListener('resize', compute);
    return () => {
      window.visualViewport?.removeEventListener('resize', compute);
      window.removeEventListener('resize', compute);
    };
  }, []);

  useEffect(() => {
    if (isPinValidated && !isLoading) {
      const from = (location.state as { from?: string })?.from || '/';
      navigate(from, { replace: true });
    }
  }, [isPinValidated, isLoading, location.state, navigate]);

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Backspace') {
        setPin((prev) => prev.slice(0, -1));
        return;
      }

      if (!/^\d$/.test(event.key)) {
        return;
      }

      setPin((prev) => {
        if (prev.length >= 6) return prev;
        return `${prev}${event.key}`;
      });
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, []);

  const handleDigitClick = (digit: string) => {
    setPin((prev) => {
      if (prev.length >= 6) return prev;
      return `${prev}${digit}`;
    });
  };

  const handleClear = () => {
    setPin('');
  };

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (pin.length !== 6 || isSubmitting) {
      return;
    }

    const result = await submitPin({ pin });

    if (result.success) {
      handleClear();
      return;
    }

    setShake(true);
    setTimeout(() => setShake(false), 400);
    handleClear();
    navigate(result.redirectUrl || helloUrl, { replace: true });
  };

  const pinDigits = useMemo(() => {
    const digits = pin.split('');
    while (digits.length < 6) {
      digits.push('');
    }
    return digits;
  }, [pin]);

  const lockedUntilLabel = formatLockedUntil(lockedUntil);

  if (isLoading) {
    return (
      <div className="min-h-dvh bg-gradient-to-br from-blue-50 via-white to-indigo-50 flex items-center justify-center">
        <div className="bg-white/80 backdrop-blur-md p-8 rounded-2xl shadow-lg">
          <div className="flex items-center justify-center">
            <Loader2 className="h-8 w-8 animate-spin text-blue-600" />
            <span className="ml-3 text-gray-600">Preparing PIN gate...</span>
          </div>
        </div>
      </div>
    );
  }

  if (lockedUntil && lockedUntilLabel) {
    return (
      <div className="min-h-dvh bg-gradient-to-br from-blue-50 via-white to-indigo-50 flex items-center justify-center px-4">
        <div className="max-w-md w-full bg-white/90 backdrop-blur-md p-8 rounded-3xl shadow-2xl border border-blue-50">
          <div className="flex flex-col items-center text-center space-y-4">
            <ShieldCheck className="h-14 w-14 text-blue-500" />
            <h1 className="text-2xl font-bold text-gray-900">
              PIN Temporarily Locked
            </h1>
            <p className="text-gray-600">
              Too many incorrect attempts. The keypad will unlock at{' '}
              <span className="font-semibold text-blue-600">
                {lockedUntilLabel}
              </span>
              .
            </p>
            <button
              type="button"
              onClick={() => refreshStatus()}
              className="inline-flex items-center gap-2 mt-3 px-4 py-2 rounded-full bg-blue-600 text-white hover:bg-blue-700 transition-colors"
            >
              <RotateCcw className="h-4 w-4" />
              Try Again
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div
      className="min-h-dvh bg-gradient-to-br from-blue-50 via-white to-indigo-50 flex items-center justify-center px-4 overflow-y-auto"
      style={{ paddingTop: 'env(safe-area-inset-top)', paddingBottom: 'max(env(safe-area-inset-bottom), 1rem)' }}
    >
      <div className={`pin-card max-w-md w-full bg-white/90 backdrop-blur-md rounded-3xl shadow-2xl border border-blue-50 my-auto ${compact ? 'p-4' : 'p-6 sm:p-8'}`}>
        <div className={`flex flex-col items-center text-center space-y-3 sm:space-y-4 ${tight ? 'mb-3' : 'mb-5 sm:mb-8'}`}>
          <div className="relative">
            <div className="absolute -inset-3 rounded-full bg-blue-100 blur opacity-75" />
            <div className="relative w-16 h-16 rounded-full bg-blue-600 flex items-center justify-center text-white shadow-lg">
              <ShieldCheck className="h-8 w-8" />
            </div>
          </div>
          <h1 className={`font-bold text-gray-900 ${compact ? 'text-2xl' : 'text-3xl'}`}>
            Enter Access PIN
          </h1>
          <p className="text-gray-600">
            A quick security check before we bring you to your passkeys.
          </p>
        </div>

        <form onSubmit={handleSubmit} className={compact ? 'space-y-4' : 'space-y-6 sm:space-y-8'}>
          <div className="flex justify-center">
            <div
              className={`pin-compact-grid grid grid-cols-6 ${compact ? 'gap-2' : 'gap-3'} ${shake ? 'animate-shake' : ''}`}
            >
              {pinDigits.map((digit, index) => (
                <div
                  key={index}
                  className={`rounded-2xl bg-slate-100 flex items-center justify-center font-semibold text-slate-700 shadow-inner ${compact ? 'w-10 h-11 text-xl' : 'w-12 h-14 text-2xl'}`}
                >
                  {digit ? '•' : ''}
                </div>
              ))}
            </div>
          </div>

          {attemptsRemaining !== null && attemptsRemaining < 5 && (
            <div className="text-center text-sm text-gray-500">
              {attemptsRemaining > 0 ? (
                <span>
                  {attemptsRemaining} attempt
                  {attemptsRemaining === 1 ? '' : 's'} remaining.
                </span>
              ) : (
                <span className="text-red-500">
                  No attempts left. Further tries will lock the keypad.
                </span>
              )}
            </div>
          )}

          <div className={`grid grid-cols-3 ${compact ? 'gap-2' : 'gap-3'}`}>
            {DIGIT_BUTTONS.slice(0, 9).map((digit) => (
              <button
                key={digit}
                type="button"
                onClick={() => handleDigitClick(digit)}
                disabled={pin.length >= 6 || isSubmitting}
                className={`rounded-2xl bg-gradient-to-br from-blue-500 to-blue-600 text-white font-semibold shadow-lg hover:from-blue-600 hover:to-blue-700 focus:ring-2 focus:ring-blue-400 focus:ring-offset-2 disabled:opacity-40 disabled:cursor-not-allowed transition-all ${compact ? 'h-12 text-xl' : 'h-16 text-2xl'}`}
              >
                {digit}
              </button>
            ))}

            <div className={`rounded-2xl bg-slate-100 flex items-center justify-center text-slate-400 font-semibold shadow-inner ${tight ? 'h-12 text-lg' : 'h-16 text-xl'}`}>
              <Sun className="h-6 w-6" />
            </div>

            <button
              type="button"
              onClick={() => handleDigitClick('0')}
              disabled={pin.length >= 6 || isSubmitting}
              className={`rounded-2xl bg-gradient-to-br from-blue-500 to-blue-600 text-white font-semibold shadow-lg hover:from-blue-600 hover:to-blue-700 focus:ring-2 focus:ring-blue-400 focus:ring-offset-2 disabled:opacity-40 disabled:cursor-not-allowed transition-all ${compact ? 'h-12 text-xl' : 'h-16 text-2xl'}`}
            >
              0
            </button>

            <button
              type="button"
              onClick={handleClear}
              disabled={pin.length === 0 || isSubmitting}
              className={`rounded-2xl bg-slate-200 text-slate-700 font-medium shadow-inner hover:bg-slate-300 focus:ring-2 focus:ring-blue-400 focus:ring-offset-2 disabled:opacity-40 disabled:cursor-not-allowed transition-all ${compact ? 'h-12' : 'h-16'}`}
            >
              Clear
            </button>
          </div>

          <button
            type="submit"
            disabled={pin.length !== 6 || isSubmitting}
            className={`w-full rounded-2xl bg-blue-600 ${tight ? 'h-12' : 'h-14'} text-white text-lg font-semibold shadow-lg hover:bg-blue-700 focus:ring-2 focus:ring-blue-400 focus:ring-offset-2 disabled:opacity-40 disabled:cursor-not-allowed transition-all flex items-center justify-center gap-2`}
          >
            {isSubmitting ? (
              <>
                <Loader2 className="h-5 w-5 animate-spin" />
                Verifying PIN...
              </>
            ) : (
              <>
                <Lock className="h-5 w-5" />
                Unlock
              </>
            )}
          </button>
        </form>
      </div>
    </div>
  );
};

export default PinPage;
